import * as React from "react";
import * as ReactQuery from "react-query";
import { UseQueryResult } from "react-query";

import { MyBagTotals_Response } from "../../model";

import {
  DataSource_MyBag_Totals_QueryKey,
  DataSource_MyBag_Totals_QueryKeyType,
} from "./data-source--mybag-totals-query-key-factory";
import {
  GenericDataSource_CapableOfBeingIntersected,
  InitialStateMinimumRequirements_GraphQl,
  useLocalizationProvider,
} from "../../../Common";

interface DataSourceMyBagTotalsQueryConsumerInterface
  extends GenericDataSource_CapableOfBeingIntersected<MyBagTotals_Response> {
  data: UseQueryResult<MyBagTotals_Response>["data"];
  status: UseQueryResult<MyBagTotals_Response>["status"];
}
const defaultValue =
  {} as unknown as DataSourceMyBagTotalsQueryConsumerInterface;
const DataSourceMyBagTotalsQueryContext = React.createContext(defaultValue);

export interface InitialStateMinimumRequirements_MyBag_Totals_DataSource
  extends InitialStateMinimumRequirements_GraphQl {}

export interface DataSourceMyBagTotalsQueryProviderProps {
  isCustomerLoggedIn: boolean;
  cartId?: string;

  useInitialState: () => {
    initialState: InitialStateMinimumRequirements_MyBag_Totals_DataSource;
  };
}
/**
 * Phase 2 payload fragment for the totals-information call, or null when the
 * phase 1 response is already consistent.
 *
 * Phase 1 posts NO shipping method: 2.4.7 TotalsInformationManagement::calculate()
 * only persists (save()s) the totals address when the payload carries both
 * method codes, so a method-less call can never corrupt the quote (the old
 * hardcoded "flatrate" persisted a phantom $15 on free-shipping carts). Its
 * response still carries the backend free-shipping decision
 * (extension_attributes.free_shipping_available) plus figures computed from
 * whatever method is currently persisted on the quote. When those two
 * disagree (fresh cart, quote infected by an old bundle, qualification
 * changed), phase 2 re-posts with the backend-decided method so the persisted
 * method and the returned figures become authoritative.
 */
export const figureOut_shippingMethodCorrection = (
  totals: MyBagTotals_Response
): { shipping_carrier_code: string; shipping_method_code: string } | null => {
  const freeShippingAvailable =
    totals.extension_attributes?.free_shipping_available === true;
  const shipping = totals.base_shipping_incl_tax;

  const isConsistent = freeShippingAvailable ? shipping === 0 : shipping > 0;
  if (isConsistent) {
    return null;
  }

  const methodCode = freeShippingAvailable ? "freeshipping" : "flatrate";
  return {
    shipping_carrier_code: methodCode,
    shipping_method_code: methodCode,
  };
};

export const DataSourceMyBagTotalsQueryProvider: React.FunctionComponent<
  DataSourceMyBagTotalsQueryProviderProps
> = (props) => {
  const { initialState } = props.useInitialState();

  const { countryCode } = useLocalizationProvider();

  const queryKey = React.useMemo(() => {
    return DataSource_MyBag_Totals_QueryKey.mybagTotals(props);
  }, [props, props.cartId, props.isCustomerLoggedIn]);

  const postTotalsInformation = (
    serviceUrl: string,
    addressInformation: Record<string, unknown>
  ) => {
    return new Promise<MyBagTotals_Response>((resolve, reject) => {
      (window as any).require(["mage/storage"], (storage: any) => {
        storage
          .post(serviceUrl, JSON.stringify({ addressInformation }))
          .done(function (response: any) {
            if (response) {
              resolve(response as MyBagTotals_Response);
            } else {
              reject();
            }
          })
          .fail(function () {
            reject();
          });
      });
    });
  };

  const fetchCartTotals = async (serviceUrl: string) => {
    const countryCodeUppercase = countryCode.toUpperCase();

    const addressInformation = {
      address: {
        // Only country_id is sent. 2.4.7 TotalsInformationManagement::calculate()
        // save()s this address onto quote_address (merge via
        // Quote::setShippingAddress), so a literal "null" postcode/region here
        // corrupted real orders. Country-level GST needs no postcode/region.
        country_id: `${countryCodeUppercase}`,
      },
    };

    const firstResponse = await postTotalsInformation(
      serviceUrl,
      addressInformation
    );

    const shippingMethodCorrection =
      figureOut_shippingMethodCorrection(firstResponse);
    if (!shippingMethodCorrection) {
      return firstResponse;
    }

    // Phase 2 only persists the corrected method onto the quote; the phase 1
    // figures are the best AVAILABLE fallback, not authoritative — on this
    // (inconsistent) branch they still reflect the stale persisted method
    // until the next successful totals fetch corrects them, and money
    // self-heals at place order. Rejecting instead would be worse: blank
    // totals AND react-query's default retry re-running both phases.
    return postTotalsInformation(serviceUrl, {
      ...addressInformation,
      ...shippingMethodCorrection,
    }).catch((error) => {
      // Breadcrumb — a systematic phase-2 failure would otherwise silently
      // mask the phantom-shipping family this fix exists to kill.
      console.warn(
        "mybag totals: phase-2 method correction failed; showing phase-1 figures",
        error
      );
      return firstResponse;
    });
  };

  const fetchGuestCartTotals = (
    desiredBag: DataSource_MyBag_Totals_QueryKeyType[1]
  ) => {
    return fetchCartTotals(
      `/rest/${initialState.store_code}/V1/guest-carts/${desiredBag.cartId}/totals-information`
    );
  };

  const fetchMemberCartTotals = (
    desiredBag: DataSource_MyBag_Totals_QueryKeyType[1]
  ) => {
    return fetchCartTotals(
      `/rest/${initialState.store_code}/V1/carts/mine/totals-information`
    );
  };

  const queryFn: ReactQuery.QueryFunction<
    MyBagTotals_Response,
    typeof queryKey
  > = async (context: ReactQuery.QueryFunctionContext<typeof queryKey>) => {
    const [baseQuery, desiredBag] = context.queryKey;

    let fetcher = fetchGuestCartTotals;
    const isCustomerLoggedIn = desiredBag.isCustomerLoggedIn;
    if (isCustomerLoggedIn) {
      fetcher = fetchMemberCartTotals;
    }

    if (!isCustomerLoggedIn && !desiredBag.cartId) {
      throw new Error("Cannot request mybag totals data without a cartId.");
    }

    const response = await fetcher(desiredBag);

    return response;
  };

  const { status, data, dataUpdatedAt, errorUpdatedAt, error, ...result } =
    ReactQuery.useQuery(queryKey, queryFn, { refetchOnWindowFocus: false });

  const value: DataSourceMyBagTotalsQueryConsumerInterface = React.useMemo(
    () => ({
      status,
      data,
      dataUpdatedAt,
      errorUpdatedAt,
      error,
    }),
    [status, data, dataUpdatedAt, errorUpdatedAt, error]
  );
  return (
    <DataSourceMyBagTotalsQueryContext.Provider value={value}>
      {props?.children || []}
    </DataSourceMyBagTotalsQueryContext.Provider>
  );
};

export const useDataSourceMyBagTotalsQuery = () => {
  const context = React.useContext(DataSourceMyBagTotalsQueryContext);

  /**
   * Normally we do this pattern below for using any provider,
   * this here is a special case where
   * we want the defaultValue to be used when this provider isn't in the tree,
   * because it can be used when there is a fully formed request information packet,
   * which isn't guaranteed to be available immediately.
   */
  // if (context === defaultValue) {
  //   throw new Error(
  //     "useDataSourceMyBagTotals must be used within a DataSourceMyBagTotalsQueryProvider"
  //   );
  // }

  return context;
};
